import { CSSObject, debounce, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  useCreateTransactionMutation,
  useGetConfigurationQuery,
  useGetDashboardOperationsQuery,
  useGetRecipientsQuery,
} from '@/api/v1';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { FlexibleSelectTextField } from '@/components/atoms/FlexibleSelectTextField';
import { AddObjectButton } from '@/components/molecules/AddObjectButton';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { Section } from '@/components/molecules/Section';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import {
  FF_DASHBOARD_NEW_BRL1_BALANCES,
  FF_DASHBOARD_NEW_MXNB_BALANCES,
} from '@/data/featureFlags';
import { useBalances } from '@/hooks/useBalances';
import { useCustomer } from '@/hooks/useCustomer';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  destinationChanged,
  fromAmountChanged,
  fromCurrencyChanged,
  initialState,
  sameDayAchChanged,
  toAmountChanged,
  transactionFlowChanged,
  WithdrawalFlowAction,
  withdrawalFlowReducer,
  WithdrawalFlowState,
} from '@/Screens/Dashboard/Flows/WithdrawalFlow/reducer';
import { DetailedBalance } from '@/types/bankInfo';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { Page } from '@/types/page';
import { RecipientResponse } from '@/types/recipient';
import { MarketExecutionType } from '@/types/transaction';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import {
  getRecipientAccountNumber,
  getRecipientCurrency,
  getRecipientDisplayName,
  isRecipientAvailableForTransaction,
} from '@/utility/recipientDisplayAdapter';
import { AsAnyActionArg } from '@/utility/reducers';

enum FormErrors {
  MISSING_ACCOUNT = 'MISSING_ACCOUNT',
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

const EXCLUDED_FROM_CURRENCIES: readonly CurrencyCodeType[] = [
  CurrencyCodeType.BRL,
  CurrencyCodeType.MXN,
];

export const WithdrawalFlow: React.FC = (): React.ReactElement => {
  const customer = useCustomer();
  const isMxnbBalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_MXNB_BALANCES);
  const isBrl1BalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_BRL1_BALANCES);

  const balanceCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] => [
      CurrencyCodeType.USD,
      CurrencyCodeType.USDC,
      CurrencyCodeType.USDT,
      ...(isMxnbBalancesEnabled ? [CurrencyCodeType.MXNB] : []),
      ...(isBrl1BalancesEnabled ? [CurrencyCodeType.BRL1] : []),
    ],
    [isMxnbBalancesEnabled, isBrl1BalancesEnabled],
  );

  const balances = useBalances({
    currencies: balanceCurrencies,
  });

  const toast = useToast();
  const tr = useTranslator();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();
  const location = useLocation();
  const [error, setError] = React.useState<FormErrors | null>(null);

  const { data: operations = [], isFetching: fetchingTransactionFlows } =
    useGetDashboardOperationsQuery('TRANSFER');

  const excludedFromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] => [
      ...EXCLUDED_FROM_CURRENCIES,
      ...(isMxnbBalancesEnabled ? [] : [CurrencyCodeType.MXNB]),
      ...(isBrl1BalancesEnabled ? [] : [CurrencyCodeType.BRL1]),
    ],
    [isMxnbBalancesEnabled, isBrl1BalancesEnabled],
  );

  const withdrawalOperations = React.useMemo(
    (): readonly DashboardOperation[] =>
      operations.filter(
        (operation: DashboardOperation): boolean =>
          !excludedFromCurrencies.includes(operation.currency),
      ),
    [operations, excludedFromCurrencies],
  );

  const { data: recipientsPage = Page.empty<RecipientResponse>(), isFetching: fetchingAccounts } =
    useGetRecipientsQuery(
      { beneficiaryId: customer.beneficiaryId, belongsToBeneficiary: true, size: 200 },
      { skip: !customer.beneficiaryId },
    );

  const reducer = React.useReducer<WithdrawalFlowState, AsAnyActionArg<WithdrawalFlowAction>>(
    withdrawalFlowReducer,
    initialState,
  );

  const [createTransaction, mutation] = useCreateTransactionMutation();
  const [state, dispatch] = reducer;
  const { destination, fromAmount, fromCurrency, toAmount, toCurrency, paymentRail, sameDayAch } =
    state;

  const [simulation, fetchingSimulation] = useSimulation({
    fromAmount: fromAmount,
    toAmount: toAmount,
    simulationId: null,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: null,
    paymentRail: paymentRail,
    destination: destination,
  });

  const recentlyAddedAccountId = React.useMemo((): string | null => {
    const { state } = location;
    return state?.accountId ?? null;
  }, [location]);

  const currentBalance = React.useMemo(
    (): DetailedBalance => balances[fromCurrency] ?? DetailedBalance.zero(fromCurrency),
    [balances, fromCurrency],
  );

  React.useEffect((): VoidFunction | void => {
    if (withdrawalOperations.length > 0) {
      dispatch(transactionFlowChanged(withdrawalOperations[0]));
    }
  }, [dispatch, withdrawalOperations]);

  const navigate = useNavigate();
  const onClose = React.useCallback((): void => {
    navigate('/home');
  }, [navigate]);

  const availableToCurrencies = React.useMemo((): readonly CurrencyCodeType[] => {
    const currencies = new Set<CurrencyCodeType>();
    operations.forEach((op: DashboardOperation): void => {
      op.destinations.forEach((dest: DashboardOperationDestination): void => {
        currencies.add(dest.currency);
      });
    });
    return Array.from(currencies);
  }, [operations]);

  const accounts = React.useMemo((): readonly RecipientResponse[] => {
    const { content } = recipientsPage;
    return content.filter((recipient: RecipientResponse): boolean => {
      if (!isRecipientAvailableForTransaction(recipient)) {
        return false;
      }

      return availableToCurrencies.includes(recipient.currency);
    });
  }, [recipientsPage, availableToCurrencies]);

  const destinationRenderer = React.useCallback((recipient: RecipientResponse): React.ReactNode => {
    const accountNumber = getRecipientAccountNumber(recipient);
    const currency = getRecipientCurrency(recipient);

    return (
      <MenuItem key={recipient.id} value={recipient.id}>
        <Box>
          <Typography variant="body2">{getRecipientDisplayName(recipient)}</Typography>
          {accountNumber && (
            <Typography variant="body1" color="text.secondary">
              {accountNumber}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary">
            {currency} - {recipient.type}
          </Typography>
        </Box>
      </MenuItem>
    );
  }, []);

  const currencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return withdrawalOperations
      .filter((operation: DashboardOperation): boolean =>
        operation.destinations.some(
          (destination: DashboardOperationDestination): boolean =>
            destination.currency === toCurrency,
        ),
      )
      .map((operation: DashboardOperation): CurrencyCodeType => operation.currency);
  }, [toCurrency, withdrawalOperations]);

  const loading = React.useMemo(
    (): boolean =>
      fetchingAccounts ||
      fetchingSimulation ||
      fetchingTransactionFlows ||
      mutation.status === QueryStatus.pending,
    [fetchingAccounts, fetchingSimulation, fetchingTransactionFlows, mutation.status],
  );

  const accountLabel = React.useMemo((): React.ReactNode => {
    if (fetchingAccounts) {
      return <Box sx={styles.loadingText} />;
    } else if (accounts.length === 0) {
      return tr('NO_ACCOUNTS');
    } else {
      return tr('SELECT_ACCOUNT');
    }
  }, [accounts.length, fetchingAccounts, tr]);

  const onAccountChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (error === FormErrors.MISSING_ACCOUNT) {
        setError(null);
      }

      const recipient = accounts.find(
        (recipient: RecipientResponse): boolean => recipient.id === event.target.value,
      );
      if (recipient) {
        // Determine the recipient's currency
        const recipientCurrency = getRecipientCurrency(recipient);

        // Find an operation that has this currency as a destination
        const matchingOperation = withdrawalOperations.find((op: DashboardOperation): boolean =>
          op.destinations.some(
            (dest: DashboardOperationDestination): boolean => dest.currency === recipientCurrency,
          ),
        );

        if (matchingOperation) {
          // Find the matching destination and move it to the front
          const matchingDestination = matchingOperation.destinations.find(
            (dest: DashboardOperationDestination): boolean => dest.currency === recipientCurrency,
          );

          if (matchingDestination) {
            const otherDestinations = matchingOperation.destinations.filter(
              (dest: DashboardOperationDestination): boolean => dest.currency !== recipientCurrency,
            );

            const reorderedOperation = {
              ...matchingOperation,
              destinations: [matchingDestination, ...otherDestinations],
            };

            // Update the operation to set the correct currencies
            dispatch(transactionFlowChanged(reorderedOperation));
          }
        }

        dispatch(destinationChanged(recipient));
      }
    },
    [dispatch, accounts, error, setError, withdrawalOperations],
  );

  const ready = React.useMemo((): boolean => destination !== '', [destination]);
  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE || error === FormErrors.MISSING_AMOUNT) {
        setError(null);
      }

      dispatch(fromAmountChanged(value));
    },
    [error, setError, dispatch],
  );

  const onToAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE || error === FormErrors.MISSING_AMOUNT) {
        setError(null);
      }

      dispatch(toAmountChanged(value));
    },
    [error, setError, dispatch],
  );

  const onConfirmWithdrawal = React.useCallback((): void => {
    const effectiveFromAmount = fromAmount ?? simulation.from.value;

    if (!effectiveFromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }
    if (effectiveFromAmount > currentBalance.available) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }

    if (simulation.id) {
      createTransaction({
        simulationId: simulation.id,
        beneficiaryIp: '0.0.0.0',
        marketExecutionType: MarketExecutionType.executeImmediately,
        achSameDay: sameDayAch || undefined,
      });
    }
  }, [
    fromAmount,
    simulation.from.value,
    currentBalance,
    setError,
    createTransaction,
    simulation.id,
    sameDayAch,
  ]);

  const computedFromAmount = React.useMemo((): number | null => {
    if (simulation.id === null) {
      return fromAmount;
    } else {
      return simulation.from.value;
    }
  }, [simulation.id, simulation.from.value, fromAmount]);

  const computedToAmount = React.useMemo((): number | null => {
    if (simulation.id === null) {
      return toAmount;
    } else {
      return simulation.to.value;
    }
  }, [simulation.id, simulation.to.value, toAmount]);

  const onSetMaxAmount = React.useCallback((): void => {
    onFromAmountChange(currentBalance.available);
  }, [onFromAmountChange, currentBalance.available]);

  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedBalance = React.useMemo((): string => {
    return formatter.format(currentBalance.available);
  }, [formatter, currentBalance]);

  const onFromCurrencyChange = React.useCallback(
    (currency: CurrencyCodeType): void => {
      dispatch(fromCurrencyChanged(currency));
    },
    [dispatch],
  );

  const onSameDayAchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      dispatch(sameDayAchChanged(event.target.checked));
    },
    [dispatch],
  );

  const isAchWithdrawal = React.useMemo(
    (): boolean => paymentRail === PaymentRailType.ACH,
    [paymentRail],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      navigate('/home/withdraw/completed');
    }
    if (mutation.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [mutation.status, toast, navigate, tr]);

  React.useEffect((): void => {
    if (recentlyAddedAccountId) {
      const recipient = accounts.find(
        (recipient: RecipientResponse): boolean => recipient.id === recentlyAddedAccountId,
      );

      if (recipient) {
        dispatch(destinationChanged(recipient));
      }
    }
  }, [accounts, dispatch, recentlyAddedAccountId, onAccountChange]);

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr('AMOUNT_BIGGER_THAN_BALANCE', formattedBalance),
        )
        .otherwise((): null => null),
    [error, formattedBalance, tr],
  );

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours) ||
      isBRLOperationOutsideMarketHours(toCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
    toCurrency,
  ]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title={tr('WITHDRAW_FUNDS')} onClose={onClose} />
      <FullScreenModalContainer>
        <Section
          title={tr('WITHDRAWAL_ACCOUNT')}
          visible={true}
          working={mutation.status === QueryStatus.pending}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <FlexibleSelectTextField
              label={accountLabel}
              select={true}
              fullWidth={true}
              disabled={loading}
              value={destination}
              error={error === FormErrors.MISSING_ACCOUNT}
              helperText={error === FormErrors.MISSING_ACCOUNT ? tr('REQUIRED_ACCOUNT') : null}
              onChange={onAccountChange}>
              {accounts.map(destinationRenderer)}
            </FlexibleSelectTextField>
            <Box py={3}>
              <AddObjectButton label={tr('ADD_NEW_WITHDRAWAL_ACCOUNT')} path="account" />
            </Box>
          </Box>
        </Section>
        <Section
          title={tr('AMOUNT')}
          visible={destination !== ''}
          working={loading}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              label={tr('YOU_WITHDRAW')}
              currencies={currencies}
              amount={computedFromAmount}
              currency={fromCurrency}
              disabled={!ready}
              helperText={amountHelperText}
              error={!!amountHelperText}
              onAmountChange={debounce(onFromAmountChange, 300)}
              onCurrencyChange={onFromCurrencyChange}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" color="custom.LightGray">
                {displayUsdcAsUsd
                  ? tr('WALLET_BALANCE_LEGACY', formattedBalance)
                  : tr('WALLET_BALANCE_CURRENCY', formattedBalance, fromCurrency)}
              </Typography>
              <Button
                variant="dark"
                size="small"
                sx={styles.maxButton}
                disabled={!ready}
                onClick={onSetMaxAmount}>
                {tr('MAX')}
              </Button>
            </Box>
            <AmountAndCurrencyWidget
              label={tr('YOU_RECEIVE')}
              currencies={[toCurrency]}
              amount={computedToAmount}
              currency={toCurrency}
              disabled={!ready}
              onAmountChange={debounce(onToAmountChange, 300)}
            />
            {isAchWithdrawal && (
              <Box sx={styles.sameDayAchContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sameDayAch}
                      onChange={onSameDayAchChange}
                      disabled={!ready || loading}
                    />
                  }
                  label={tr('SAME_DAY_ACH')}
                  sx={styles.sameDayAchLabel}
                />
              </Box>
            )}
            {shouldNotAllowOperation && <OutsideBRLMarketHoursAlert />}
            <StyledAccordion disabled={simulation.id === null}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="h7">{tr('TRANSACTION_DETAILS')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TransactionDetails
                  transactionDetails={simulation.transactionDetails}
                  fromAmount={simulation.from.value}
                  fromCurrency={fromCurrency}
                  toAmount={simulation.to.value}
                  toCurrency={toCurrency}
                  validityTimestamp={simulation.expiresAt}
                />
              </AccordionDetails>
            </StyledAccordion>

            <Box sx={styles.expectedSettlementTimeInfo}>
              <ExpectedSettlementTimeInfo
                expectedSettlementTimeDetails={
                  simulation.transactionDetails.expectedSettlementTimeDetails
                }
              />
            </Box>
          </Box>
        </Section>
        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            loading={loading}
            disabled={simulation.id === null || shouldNotAllowOperation}
            onClick={onConfirmWithdrawal}>
            {tr('CONFIRM_WITHDRAWAL')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>

      <Outlet />
    </ApplicationDialog>
  );
};

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  maxButton: {
    padding: 0,
    width: 53,
    minWidth: 0,
  },
  loadingText: {
    my: 0.75,
    height: 16,
    width: 240,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
  sameDayAchContainer: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 4,
    px: 2,
    py: 1.5,
    width: '100%',
  },
  sameDayAchLabel: {
    m: 0,
    alignItems: 'center',
  },
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};
